import { createFileRoute, Link } from "@tanstack/react-router";
import { TackLogo } from "@/components/TackLogo";
import { SiteFooter } from "@/components/SiteFooter";

export const Route = createFileRoute("/dpa")({
  head: () => ({
    meta: [
      { title: "Tack. Data Processing Agreement" },
      {
        name: "description",
        content:
          "GDPR-compliant Data Processing Agreement between Lineri AB and merchants using Tack.",
      },
      { property: "og:title", content: "Tack. Data Processing Agreement" },
      {
        property: "og:description",
        content: "DPA covering processing of end-customer data via Tack.",
      },
    ],
    links: [{ rel: "canonical", href: "https://tack.company/dpa" }],
  }),
  component: DpaPage,
});

function DpaPage() {
  return (
    <div className="min-h-screen bg-background">
      <nav className="border-b border-border">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <Link to="/" aria-label="Tack home" className="inline-block hover:opacity-80 transition-opacity">
            <TackLogo size="md" />
          </Link>
        </div>
      </nav>

      <main className="max-w-3xl mx-auto px-4 py-12">
        <h1 className="text-3xl font-display font-bold mb-2">
          Data Processing Agreement
        </h1>
        <p className="text-sm text-muted-foreground mb-6">
          Last updated: {new Date().toLocaleDateString("en-SE")}
        </p>

        <div className="rounded-lg border border-border bg-tack-warm/40 p-4 mb-8 text-sm leading-relaxed">
          This DPA is incorporated by reference into our{" "}
          <Link to="/terms" className="text-primary underline">
            Terms of Service
          </Link>{" "}
          for any merchant who processes EU/EEA customer data via Tack. By
          signing up for and using Tack you accept this DPA — no separate
          signature is required. A countersigned PDF version is available on
          request: email{" "}
          <a
            href="mailto:hello@tack.company"
            className="text-primary underline"
          >
            hello@tack.company
          </a>
          .
        </div>

        <div className="prose prose-sm max-w-none space-y-6 text-foreground/90 leading-relaxed">
          <section>
            <h2 className="text-xl font-display font-semibold mb-3">
              1. Definitions
            </h2>
            <p>
              Capitalised terms not defined here have the meanings given in the
              EU General Data Protection Regulation (Regulation 2016/679,
              "GDPR").
            </p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li>
                <strong>"Controller"</strong> — the merchant using Tack,
                determining the purposes and means of processing customer
                personal data.
              </li>
              <li>
                <strong>"Processor"</strong> — Lineri AB, processing personal
                data on behalf of the Controller.
              </li>
              <li>
                <strong>"Data Subject"</strong> — the merchant's end customer
                who scans a Tack QR and submits feedback.
              </li>
              <li>
                <strong>"Personal Data"</strong> — any information relating to
                an identified or identifiable Data Subject as defined in GDPR
                Art. 4.
              </li>
              <li>
                <strong>"Sub-processor"</strong> — a third party engaged by the
                Processor to process Personal Data.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-display font-semibold mb-3">
              2. Subject matter and duration
            </h2>
            <p>
              This DPA governs the Processor's processing of Personal Data on
              behalf of the Controller in connection with the Tack service. It
              remains in force for as long as the Controller's Tack account is
              active, plus any retention period required by law.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-display font-semibold mb-3">
              3. Nature and purpose of processing
            </h2>
            <p>The Processor processes Personal Data to:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Collect customer feedback (rating + optional comment) on behalf of the Controller.</li>
              <li>Issue and email one-time discount codes to the customer.</li>
              <li>Send "We Miss You" retention nudges from the Controller (if enabled).</li>
              <li>Provide aggregated analytics to the Controller.</li>
              <li>Operate, secure, and improve the Tack service.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-display font-semibold mb-3">
              4. Categories of Data Subjects
            </h2>
            <p>The Controller's end customers who scan a Tack QR code at the Controller's location.</p>
          </section>

          <section>
            <h2 className="text-xl font-display font-semibold mb-3">
              5. Categories of Personal Data
            </h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>Email address</li>
              <li>Star rating (1–5)</li>
              <li>Optional free-text comment</li>
              <li>Submission timestamp</li>
              <li>Truncated IP address and device/browser type (for fraud prevention and rate limiting)</li>
            </ul>
            <p className="mt-2 text-muted-foreground">
              No special categories of data (GDPR Art. 9) are processed.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-display font-semibold mb-3">
              6. Processor obligations (Art. 28 GDPR)
            </h2>
            <p>The Processor shall:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Process Personal Data only on documented instructions from the Controller (the Controller's configuration of Tack constitutes such instructions).</li>
              <li>Ensure persons authorised to process the data are bound by confidentiality.</li>
              <li>Implement appropriate technical and organisational measures (see section 9).</li>
              <li>Engage Sub-processors only under section 7 below.</li>
              <li>Assist the Controller in responding to Data Subject requests (access, rectification, erasure, portability, objection).</li>
              <li>Assist with security, breach notification, impact assessments, and prior consultation with supervisory authorities.</li>
              <li>Notify the Controller without undue delay and within 72 hours of becoming aware of a Personal Data breach affecting the Controller's data.</li>
              <li>At the Controller's choice, delete or return all Personal Data at the end of provision of services, and delete existing copies (subject to legal retention requirements).</li>
              <li>Make available all information necessary to demonstrate compliance and allow for audits, conducted by the Controller or a mandated auditor at reasonable intervals and with reasonable prior notice (up to once per year, or more often in case of a breach).</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-display font-semibold mb-3">
              7. Sub-processors
            </h2>
            <p>
              The Controller authorises the Processor to engage the
              Sub-processors listed in our{" "}
              <Link to="/privacy" className="text-primary underline">
                Privacy Policy
              </Link>
              . The Processor will give at least 30 days' notice of any
              intended changes (addition or replacement) and the Controller
              may object on reasonable grounds; if no agreement is reached, the
              Controller may terminate the affected service.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-display font-semibold mb-3">
              8. International transfers
            </h2>
            <p>
              Personal Data is primarily stored within the EU/EEA via our EU
              hosting region. Where any Sub-processor processes data outside
              the EEA, the Processor relies on appropriate safeguards under
              GDPR Chapter V — typically the European Commission's Standard
              Contractual Clauses (SCCs) or an adequacy decision.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-display font-semibold mb-3">
              9. Technical and organisational measures
            </h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>Encryption in transit (TLS 1.2+) and at rest.</li>
              <li>Row-Level Security (RLS) isolating each merchant's data.</li>
              <li>Role-based access controls and least-privilege principles for staff access.</li>
              <li>Regular automated backups with point-in-time recovery.</li>
              <li>Audit logging of administrative actions.</li>
              <li>Rate limiting and fraud-detection on customer-facing endpoints.</li>
              <li>Vendor due diligence on all Sub-processors.</li>
              <li>Incident response procedures including 72-hour breach notification.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-display font-semibold mb-3">
              10. Liability and term
            </h2>
            <p>
              Liability under this DPA is governed by the limitations set out
              in our{" "}
              <Link to="/terms" className="text-primary underline">
                Terms of Service
              </Link>
              . This DPA terminates automatically when the Controller's Tack
              account is closed; provisions that by their nature should survive
              termination (confidentiality, audit, deletion) shall do so.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-display font-semibold mb-3">
              11. Governing law
            </h2>
            <p>
              This DPA is governed by the laws of Sweden. Disputes are subject
              to the exclusive jurisdiction of the Swedish courts.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-display font-semibold mb-3">
              12. Contact
            </h2>
            <p>
              For DPA-related inquiries (including audit requests, breach
              notifications, or a countersigned copy), contact{" "}
              <a
                href="mailto:hello@tack.company"
                className="text-primary underline"
              >
                hello@tack.company
              </a>
              .
            </p>
          </section>
        </div>
      </main>

      <SiteFooter variant="legal" />
    </div>
  );
}
